// Reliability report tabs: R2 breakdowns (with the honesty banner - MTTR only over complete
// malfunction windows) and R3 preventive compliance.
import React from 'react';
import { useTheme } from '../../../../core/ui/ThemeContext';
import { useLanguage } from '../../../../core/i18n/LanguageContext';
import { Card } from '../ui';
import { useReport, Kpi, BarList, csvDownload, ExportButton } from './ReportsScreen';
import { getReportBreakdowns, getReportCompliance, getReportDamages } from '../../api';

const fmt = (v) => Number(v).toLocaleString(undefined, { maximumFractionDigits: 1 });

function Ladder({ error, data, retry, children }) {
  const { isDarkMode } = useTheme();
  const { t } = useLanguage();
  const sub = isDarkMode ? 'text-white/50' : 'text-slate-500';
  if (error) return <Card><p className={sub}>{error} <button type="button" className="underline" onClick={retry}>{t('pm.common.retry')}</button></p></Card>;
  if (data === null) return <Card><p className={sub}>{t('pm.common.loading')}</p></Card>;
  return children(data);
}

/** Damage/cause Pareto — "what breaks, and why". Data exists once the wizard's code dropdowns
 *  are used; until then the card states that honestly instead of showing an empty chart. */
function DamagesCard({ me, user }) {
  const { isDarkMode } = useTheme();
  const { t } = useLanguage();
  const role = me.profile.id;
  const rep = useReport(() => getReportDamages(user, { plant: me.activePlant, days: 365 }, role), [user, me.activePlant, role]);
  const sub = isDarkMode ? 'text-white/50' : 'text-slate-500';
  if (rep.error || rep.data === null) return null;
  const d = rep.data;
  const empty = !d.topDamages.length && !d.topCauses.length;
  return (
    <Card>
      <p className="font-semibold text-sm mb-2">{t('pm.rep.pareto')}</p>
      {empty ? <p className={`text-sm ${sub}`}>{t('pm.rep.paretoEmpty')}</p> : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <p className={`text-xs mb-1 ${sub}`}>{t('pm.rep.topDamages')}</p>
            <BarList rows={d.topDamages.map((x) => ({ label: x.text || `${x.group}/${x.code}`, value: x.count, display: `${x.count}×` }))} />
          </div>
          <div>
            <p className={`text-xs mb-1 ${sub}`}>{t('pm.rep.topCauses')}</p>
            <BarList rows={d.topCauses.map((x) => ({ label: x.text || `${x.group}/${x.code}`, value: x.count, display: `${x.count}×` }))} />
          </div>
        </div>
      )}
    </Card>
  );
}

export function BreakdownsTab({ me, user }) {
  const { isDarkMode } = useTheme();
  const { t } = useLanguage();
  const role = me.profile.id;
  const rep = useReport(() => getReportBreakdowns(user, { plant: me.activePlant, days: 365 }, role), [user, me.activePlant, role]);

  return (
    <Ladder {...rep}>
      {(d) => (
        <div className="space-y-3">
          <div className="flex gap-2 flex-wrap">
            <Kpi label={t('pm.rep.breakdowns')} value={d.breakdowns} tone={d.breakdowns > 0 ? 'warn' : 'good'} />
            <Kpi label={t('pm.rep.windowDays')} value={d.windowDays} />
          </div>
          {/* Data-quality honesty: MTTR is only as good as the maintained malfunction ends. */}
          <p className={`text-xs ${isDarkMode ? 'text-white/40' : 'text-slate-400'}`}>
            {t('pm.rep.mttrNote', { n: d.completeWindows, total: d.breakdowns })}
          </p>
          <Card>
            <div className="flex justify-between mb-2">
              <p className="font-semibold text-sm">{t('pm.rep.topByBreakdowns')}</p>
              <ExportButton onClick={() => csvDownload('pm-breakdowns.csv',
                [{ key: 'equipment', label: t('pm.rep.equipment') }, { key: 'description', label: t('pm.rep.description') },
                 { key: 'breakdowns', label: t('pm.rep.breakdowns') }, { key: 'downtimeHours', label: t('pm.rep.downtime') },
                 { key: 'mttrHours', label: 'MTTR (h)' }, { key: 'mtbfDays', label: 'MTBF (d)' },
                 { key: 'completeWindows', label: t('pm.rep.completeWindows') }],
                d.topEquipment)} />
            </div>
            <BarList rows={d.topEquipment.map((e) => ({
              label: `${e.equipment || '—'} ${e.description}`,
              value: e.breakdowns,
              display: `${e.breakdowns}× · ${fmt(e.downtimeHours)} h${e.completeWindows > 0 ? ` · MTTR ${fmt(e.mttrHours)} h` : ''} · MTBF ${fmt(e.mtbfDays)} d`,
            }))} />
          </Card>
          <DamagesCard me={me} user={user} />
        </div>
      )}
    </Ladder>
  );
}

export function ComplianceTab({ me, user }) {
  const { isDarkMode } = useTheme();
  const { t } = useLanguage();
  const role = me.profile.id;
  const rep = useReport(() => getReportCompliance(user, { plant: me.activePlant }, role), [user, me.activePlant, role]);
  const sub = isDarkMode ? 'text-white/50' : 'text-slate-500';

  return (
    <Ladder {...rep}>
      {(d) => (
        <div className="space-y-3">
          <div className="flex gap-2 flex-wrap">
            <Kpi label={t('pm.rep.plans')} value={d.plans} />
            <Kpi label={t('pm.rep.openDueCalls')} value={d.openDueCalls} tone={d.openDueCalls > 0 ? 'bad' : 'good'} />
            <Kpi label={t('pm.rep.inactivePlans')} value={d.inactive} tone={d.inactive > 0 ? 'warn' : undefined} />
            <Kpi label={t('pm.rep.neverScheduled')} value={d.neverScheduled} tone={d.neverScheduled > 0 ? 'warn' : undefined} />
          </div>
          <Card>
            <div className="flex justify-between mb-2">
              <p className="font-semibold text-sm">{t('pm.rep.perPlan')}</p>
              <ExportButton onClick={() => csvDownload('pm-compliance.csv',
                [{ key: 'plan', label: t('pm.rep.plan') }, { key: 'description', label: t('pm.rep.description') },
                 { key: 'calls', label: t('pm.rep.calls') }, { key: 'withOrder', label: t('pm.rep.withOrder') },
                 { key: 'openDue', label: t('pm.rep.openDueCalls') }, { key: 'nextDue', label: t('pm.rep.nextDue') }],
                d.rows)} />
            </div>
            <div className="space-y-2 text-sm">
              {d.rows.map((r) => (
                <div key={r.plan} className={`flex items-start justify-between gap-3 py-1 border-b last:border-b-0 ${isDarkMode ? 'border-white/[0.06]' : 'border-slate-100'}`}>
                  <div className="min-w-0">
                    <span className={isDarkMode ? 'text-white/80' : 'text-slate-700'}>{r.plan} — {r.description}</span>
                    <div className={`text-xs ${sub}`}>
                      {t('pm.rep.calls')}: {r.calls} · {t('pm.rep.withOrder')}: {r.withOrder}
                      {r.nextDue ? ` · ${t('pm.rep.nextDue')}: ${r.nextDue}` : ''}
                      {r.inactive ? ` · ${t('pm.prev.deactivate')}` : ''}
                      {r.neverScheduled ? ` · ${t('pm.rep.neverScheduledOne')}` : ''}
                    </div>
                  </div>
                  <span className={`whitespace-nowrap text-xs font-semibold ${r.openDue > 0 ? (isDarkMode ? 'text-rose-300' : 'text-rose-600') : (isDarkMode ? 'text-emerald-300' : 'text-emerald-700')}`}>
                    {r.openDue > 0 ? t('pm.rep.openDueN', { n: r.openDue }) : t('pm.rep.onTrack')}
                  </span>
                </div>
              ))}
            </div>
          </Card>
        </div>
      )}
    </Ladder>
  );
}
